import { useLocalSearchParams } from 'expo-router'
import { useForm } from 'react-hook-form'
import { useJob } from '@/hooks/jobs'
import { useCreateLocation } from '@/hooks/locations'
import { useAvoidSoftInputForScreen } from '@/hooks/useAvoidSoftInputForScreen'
import { useHeaderOptions } from '@/hooks/useHeaderOptions'
import { useNavigateBack } from '@/hooks/useNavigateBack'
import { errorToString } from '@/lib/errors'
import { createDefaultLocationFormData } from '@/lib/locations'
import type { LocationMutatableData } from '@/types/locations'
import { LoadingIndicator } from '@/ui/LoadingIndicator'
import { GeofenceLimitWarning } from '@/ui/locations/GeofenceLimitWarning'
import { LocationFormFields } from '@/ui/locations/LocationFormFields'
import { NotFound } from '@/ui/NotFound'
import { Page } from '@/ui/Page'

export default function NewLocationPage() {
    useAvoidSoftInputForScreen()
    const { navigateBackTo } = useNavigateBack()
    const params = useLocalSearchParams<{ orgSlug: string; jobSlug: string }>()
    const { createLocation } = useCreateLocation()

    const form = useForm<LocationMutatableData>({
        defaultValues: createDefaultLocationFormData(),
    })

    const { job, isPending: isLoadingJob } = useJob(params.jobSlug)

    const handleSubmit = form.handleSubmit(async data => {
        if (!job) {
            form.setError('name', { type: 'manual', message: 'No job found' })
            return
        }

        try {
            form.clearErrors()
            await createLocation({ ...data, job: job.id })
            navigateBackTo(`/${params.orgSlug}/settings/jobs/${params.jobSlug}`)
        } catch (error) {
            const message = errorToString(error)
            form.setError('name', { type: 'manual', message })
        }
    })

    useHeaderOptions({
        buttons: {
            type: 'create-save',
            onPress: handleSubmit,
            loading: form.formState.isSubmitting,
        },
    })

    if (isLoadingJob) {
        return <LoadingIndicator message="Loading job..." />
    }

    if (!job) {
        return (
            <NotFound message="Job not found. It may have been deleted or you don't have permission to view it." />
        )
    }

    return (
        <Page>
            <GeofenceLimitWarning orgId={job.org} />
            <LocationFormFields control={form.control} />
        </Page>
    )
}
